import Database from 'better-sqlite3';
import { mkdirSync } from 'node:fs';
import { dirname } from 'node:path';
import { MIGRATIONS, SQLITE_SCHEMA_VERSION } from './schema.js';

export type SqliteDatabase = Database.Database;

export function openOfflineDatabase(filePath: string): SqliteDatabase {
  // better-sqlite3 does not create parent dirs; first launch / wiped ~/.incashflow-pos must mkdir.
  if (filePath !== ':memory:') {
    mkdirSync(dirname(filePath), { recursive: true });
  }
  const db = new Database(filePath);
  db.pragma('journal_mode = WAL');
  db.pragma('foreign_keys = ON');
  migrateOfflineDatabase(db);
  return db;
}

export function openInMemoryOfflineDatabase(): SqliteDatabase {
  return openOfflineDatabase(':memory:');
}

export function migrateOfflineDatabase(db: SqliteDatabase): void {
  db.exec(`CREATE TABLE IF NOT EXISTS kv_meta (key TEXT PRIMARY KEY, value TEXT NOT NULL)`);
  const row = db
    .prepare(`SELECT value FROM kv_meta WHERE key = 'schema_version'`)
    .get() as { value: string } | undefined;
  let version = row ? Number.parseInt(row.value, 10) : 0;

  while (version < SQLITE_SCHEMA_VERSION) {
    const next = version + 1;
    const sql = MIGRATIONS[next];
    if (sql) {
      db.exec(sql);
    }
    version = next;
  }

  db.prepare(
    `INSERT INTO kv_meta (key, value) VALUES ('schema_version', ?)
     ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
  ).run(String(SQLITE_SCHEMA_VERSION));
}

const OFFLINE_DATA_TABLES = [
  'products',
  'categories',
  'branch_inventory',
  'outbox_ops',
  'sales_history',
  'branches',
  'auth_profiles',
  'receipt_id_aliases',
] as const;

/** Drop catalog, pending sales, and saved cashier profiles. Keeps the schema version. */
export function wipeOfflineDatabase(db: SqliteDatabase): void {
  const wipe = db.transaction(() => {
    for (const table of OFFLINE_DATA_TABLES) {
      db.prepare(`DELETE FROM ${table}`).run();
    }
    db.prepare(`DELETE FROM sync_meta`).run();
    db.prepare(`DELETE FROM kv_meta WHERE key != 'schema_version'`).run();
  });
  wipe();
}

export function getSyncMetaVersion(db: SqliteDatabase, key: string): number {
  const row = db.prepare(`SELECT version FROM sync_meta WHERE key = ?`).get(key) as
    | { version: number }
    | undefined;
  return row?.version ?? 0;
}

export function setSyncMetaVersion(db: SqliteDatabase, key: string, version: number): void {
  db.prepare(
    `INSERT INTO sync_meta (key, version) VALUES (?, ?)
     ON CONFLICT(key) DO UPDATE SET version = excluded.version`,
  ).run(key, version);
}
